import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Main Container for the book review
const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(229, 57, 53, 0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

// Title styling
const Title = styled.h1`
  font-size: 2.5rem;
  color: #E53935;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

// Author styling
const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

// Cover Image styling
const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

// Date Published styling
const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

// Content Section styling
const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

// Section Title styling
const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #E53935;
  border-bottom: 1px solid rgba(229, 57, 53, 0.3);
  padding-bottom: 0.5rem;
`;

// Section Content styling
const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

// Quote styling
const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #E53935;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

// Personal Reflection styling
const PersonalNote = styled.div`
  width: 100%;
  max-width: 1200px;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 1.5rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  margin-top: 2rem;
  color: rgb(var(--fg) / 0.82);
  font-size: 1rem;
  line-height: 1.8;
`;

// Call-to-Action styling
const CallToAction = styled.p`
  margin-top: 1.5rem;
  color: #E53935;
  font-weight: bold;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: rgb(var(--fg) / 0.92);
  }
`;

// List Item styling
const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

// Ordered List styling
const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

// Tags Container styling
const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

// Tag styling
const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(229, 57, 53, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(229, 57, 53, 0.5);
  }
`;

export const TheWorldForSaleMetadata = {
  title: "The World For Sale",
  description: "Money, Power and the Traders Who Barter the Earth’s Resources",
  tags: ['Economics', 'Trade', 'Globalization', 'Geopolitics', 'Business'],
  author: "Javier Blas & Jack Farchy",
  datePublished: "2024-02-25",
  category: "Book Review",
  path: "/book-review/the-world-for-sale",
  coverImage: "/images/the_world_for_sale.jpg", // Ensure this path is correct
};

const TheWorldForSale = () => {
  const navigate = useNavigate();
  const metadata = TheWorldForSaleMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
          {metadata.tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            "The World For Sale" takes readers behind the scenes of the world’s commodity trading industry, uncovering
            the stories of the powerful traders who control global markets. Javier Blas and Jack Farchy explore how
            these traders shape geopolitics and economies by bartering the world's resources, revealing the power
            dynamics and intrigues of the commodities market.
          </p>
          <p>
            This book provides an eye-opening look at the interconnected world of resource trading, from oil to metals,
            and how these traders influence nations, policies, and wealth. It’s an essential read for anyone interested
            in understanding the forces behind globalization and economic power.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>Global Commodities Trade:</strong> The mechanisms behind global trade and how commodities are moved and exchanged.</ListItem>
            <ListItem><strong>Power and Influence:</strong> The political and economic power wielded by traders in controlling resources.</ListItem>
            <ListItem><strong>Geopolitics:</strong> The influence of resource trading on international politics and conflicts.</ListItem>
            <ListItem><strong>Economic Impact:</strong> How trading affects global economies and markets.</ListItem>
            <ListItem><strong>Market Manipulation:</strong> Exploring the ethical issues and manipulation tactics used in trading.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "The world is run by those who control its resources."
          </Quote>
          <Quote>
            "Traders do not just respond to the markets—they create them."
          </Quote>
        </SectionContent>
      </ContentSection>

      {/* Personal Note */}
      <PersonalNote>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            Reading this book made me think deeply about the hidden forces that drive the global economy. While I always
            knew commodity trading was influential, the real-life stories and the sheer power that traders wield took me by surprise.
            It feels unsettling to realize how much of our everyday lives are impacted by a few major players behind the scenes,
            making deals that shift the balance of entire economies.
          </p>
          <p>
            One thing that stayed with me is how interconnected politics and commodities are, and how these traders can either stabilize or disrupt economies.
            This book not only taught me about the mechanisms of trade but also raised questions about ethics and accountability in such a powerful industry.
          </p>
        </SectionContent>
      </PersonalNote>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default TheWorldForSale;
